import type { StudySwimlaneSelectDto, StudyTaskReadinessSelectDto, StudyTaskSelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Skeleton } from '@components/ui/skeleton';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { cn } from '@lib/utils';
import { useGetMeiaStudyTargetEngagements, useGetMeiaSwimlaneTasks } from '@queries/meia.query';
import { PlusIcon, SettingsIcon } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { MeiaTaskCard } from './meia-task-card.component';
import { getMeiaTaskColumnStatus } from './meia-task-column.util';
import { MeiaTaskReadinessStatusIcon } from './meia-task-readiness-status-icon.component';

type MeiaTaskColumnProps = {
  canEdit: boolean;
  onAddTask: (swimlaneId: string) => void;
  onEditStage?: () => void;
  onEditTask: (task: StudyTaskSelectDto) => void;
  studyId: string;
  swimlane: StudySwimlaneSelectDto;
  taskFilter?: (task: StudyTaskSelectDto) => boolean;
  taskReadiness?: StudyTaskReadinessSelectDto[];
};

export const MeiaTaskColumn: FC<MeiaTaskColumnProps> = (props) => {
  const { canEdit, onAddTask, onEditStage, onEditTask, studyId, swimlane, taskFilter, taskReadiness = [] } = props;
  const { data: tasks = [], isLoading } = useGetMeiaSwimlaneTasks({ studyId, swimlaneId: swimlane.id });
  const sortedTasks = useMemo(() => [...tasks].sort((a, b) => a.position - b.position), [tasks]);
  const visibleTasks = useMemo(
    () => (taskFilter ? sortedTasks.filter(taskFilter) : sortedTasks),
    [sortedTasks, taskFilter],
  );
  const { data: studyEngagements = [] } = useGetMeiaStudyTargetEngagements({ studyId });
  const taskIds = useMemo(() => visibleTasks.map((task) => task.id), [visibleTasks]);
  const engagements = useMemo(() => {
    const visibleTaskIds = new Set(taskIds);
    return studyEngagements.filter((engagement) => visibleTaskIds.has(engagement.taskId));
  }, [studyEngagements, taskIds]);
  const taskReadinessById = useMemo(() => new Map(taskReadiness.map((entry) => [entry.id, entry])), [taskReadiness]);
  const hasTasks = visibleTasks.length > 0;
  const { isOver, setNodeRef } = useDroppable({
    data: { swimlaneId: swimlane.id, type: 'column' },
    id: `column-${swimlane.id}`,
  });

  return (
    <div className="flex w-72 shrink-0 self-stretch">
      <Tile
        className="h-full w-full"
        contentClassName="h-full gap-3"
        header={{
          actions: onEditStage ? (
            <IconButton.HoverReveal
              ariaLabel={`Edit stage ${swimlane.phaseName}`}
              icon={<SettingsIcon size={14} />}
              onClick={onEditStage}
              variant="secondary"
            />
          ) : undefined,
          title: (
            <div className="flex min-w-0 items-start gap-2">
              <TaskColumnStatusIcon
                isLoading={isLoading}
                swimlane={swimlane}
                taskReadiness={taskReadiness}
                tasks={sortedTasks}
              />
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-sm font-semibold text-gray-900">
                  {swimlane.phaseName || 'Untitled Stage'}
                  <span className="ml-1.5 text-xs font-normal text-gray-400">({sortedTasks.length})</span>
                </span>
                <BlueLabel
                  className="text-xs"
                  text="Stage"
                />
              </div>
            </div>
          ),
        }}
        hoverReveal={Boolean(onEditStage)}
      >
        <StageEngagedTargetRollup engagements={engagements} />
        <SortableContext
          items={taskIds}
          strategy={verticalListSortingStrategy}
        >
          <div
            className={cn(
              'flex min-h-[260px] flex-1 flex-col gap-2 rounded-md p-1 transition-colors',
              isOver && 'bg-primary/5 ring-2 ring-primary/20',
            )}
            ref={setNodeRef}
          >
            {isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : hasTasks ? (
              <>
                {visibleTasks.map((task) => (
                  <MeiaTaskCard
                    canDrag={canEdit}
                    key={task.id}
                    onClick={() => onEditTask(task)}
                    readiness={taskReadinessById.get(task.id)}
                    studyId={studyId}
                    task={task}
                  />
                ))}
                {canEdit && (
                  <div className="mt-auto border-t border-gray-200 pt-2">
                    <Button
                      className="w-full justify-center"
                      onClick={() => onAddTask(swimlane.id)}
                      size="xs"
                      type="button"
                      variant="link"
                    >
                      <PlusIcon className="size-3.5" />
                      Add Task
                    </Button>
                  </div>
                )}
              </>
            ) : sortedTasks.length > 0 ? (
              <EmptyStateContainer title="No Matching Tasks" />
            ) : (
              <EmptyStateContainer
                message={canEdit ? '+ Add Task' : undefined}
                onClick={canEdit ? () => onAddTask(swimlane.id) : undefined}
                title="No Tasks"
              />
            )}
          </div>
        </SortableContext>
      </Tile>
    </div>
  );
};

const StageEngagedTargetRollup: FC<{ engagements: { threatTargetSystemCode: string }[] }> = (props) => {
  const { engagements } = props;
  const countsByTargetCode = useMemo(() => {
    const counts = new Map<string, number>();
    for (const engagement of engagements) {
      counts.set(engagement.threatTargetSystemCode, (counts.get(engagement.threatTargetSystemCode) ?? 0) + 1);
    }
    return counts;
  }, [engagements]);
  const targets = Array.from(countsByTargetCode.entries()).sort((first, second) => first[0].localeCompare(second[0]));

  if (targets.length === 0) return null;

  return (
    <div className="flex flex-col gap-1 rounded-md border border-rose-100 bg-rose-50/60 p-2">
      <span className="text-xs font-semibold text-rose-700">Engaged targets</span>
      <div className="flex flex-wrap gap-1">
        {targets.map(([targetCode, count]) => (
          <Badge
            key={targetCode}
            size="xs"
            variant="destructive"
          >
            {targetCode} ({count})
          </Badge>
        ))}
      </div>
    </div>
  );
};

type TaskColumnStatusIconProps = {
  isLoading: boolean;
  swimlane: StudySwimlaneSelectDto;
  taskReadiness: StudyTaskReadinessSelectDto[];
  tasks: StudyTaskSelectDto[];
};

const TaskColumnStatusIcon: FC<TaskColumnStatusIconProps> = (props) => {
  const { isLoading, swimlane, taskReadiness, tasks } = props;

  if (isLoading) {
    return (
      <span
        aria-hidden
        className="mt-0.5 inline-flex size-5 shrink-0"
      />
    );
  }

  const taskMoeCountsByTaskId = new Map(taskReadiness.map((entry) => [entry.id, entry.moeCount]));
  const taskMopCountsByTaskId = new Map(taskReadiness.map((entry) => [entry.id, entry.mopCount]));
  const taskMosCountsByTaskId = new Map(taskReadiness.map((entry) => [entry.id, entry.mosCount]));
  const status = getMeiaTaskColumnStatus({
    swimlaneMeasuresStatus: swimlane.measuresStatus,
    taskMoeCountsByTaskId,
    taskMopCountsByTaskId,
    taskMosCountsByTaskId,
    tasks,
  });
  const tooltip =
    status === 'complete'
      ? 'This Stage has MoS/MoE coverage, Tasks, MoS links, MoE links, MoP, and scoring setup.'
      : status === 'empty'
        ? 'This Stage is missing MoS coverage or Tasks.'
        : 'This Stage has incomplete MoS/MoE coverage or at least one incomplete Task.';

  return (
    <MeiaTaskReadinessStatusIcon
      className="mt-0.5"
      status={status}
      tooltip={tooltip}
    />
  );
};
